/* eslint-disable @typescript-eslint/no-unused-vars */
import clsx from 'clsx';
import { toast } from 'react-toastify';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { ParametersType, ThresholdValues } from '../constants/telosair-interfaces';
import { getParameterByName } from '../_helpers/globalHelper';
import { useData } from '../context/data.context';
import Button from '../controls/Button';
import CheckboxControl from '../controls/Checkbox';
import { DEFAULT_UNITS } from '../constants/global-constants';

type Props = {
  data: ThresholdValues;
  parameter: ParametersType;
  mode: 'auto' | 'custom' | 'copy';
  isNew?: boolean;
  onSave: (data: ThresholdValues) => void;
  onDelete: () => void;
  disabled?: boolean;
};

type ValueEditorProps = {
  id: string;
  value: string;
  onChange: (text: string) => void;
  onFocusChange: (id: string) => void;
  label: string;
  type: 'warning' | 'critical';
  min: string;
  max: string;
  checked: boolean;
  setChecked: (val: boolean) => void;
  hasError?: boolean;
};
// eslint-disable-next-line jsdoc/require-jsdoc
function ValueEditor(props: ValueEditorProps): ReactElement {
  const {
    id,
    value,
    onChange,
    label,
    type,
    min,
    max,
    checked,
    hasError,
    setChecked,
    onFocusChange
  } = props;

  // eslint-disable-next-line jsdoc/require-jsdoc
  const onValueChnage = (val: string): void => {
    // Allow only numbers and a single optional decimal point
    // const isValid = /^\d*\.?\d*$/.test(val);
    // if (!isValid) {
    //   return;
    // }

    // if (val === '') {
    //   onChange('0');
    //   return;
    // }

    onChange(val);
  };
  return (
    <div
      className={clsx('border-l-2 flex items-center justify-between px-4 w-1/2 h-9 mt-5', {
        'border-warning-500': type === 'warning',
        'border-error-500': type === 'critical'
      })}
    >
      <div className="flex items-center gap-3">
        <CheckboxControl
          id={id}
          // disabled={type === 'critical'}
          checked={checked}
          onChange={() => setChecked(!checked)}
        />
        <div className="text-sm font-medium text-gray-300">{label}</div>
      </div>
      <input
        id={`${id}input`}
        onFocus={() => onFocusChange(id)}
        onBlur={() => onFocusChange('none')}
        type="number"
        inputMode="decimal"
        step="any"
        onKeyDown={(e) => {
          if (['e', 'E', '+', '-'].includes(e.key)) {
            e.preventDefault();
          }
        }}
        disabled={!checked}
        min={min}
        max={max}
        className={clsx('h-9 w-12 rounded bg-container-800 border text-center', {
          'text-gray-300': checked,
          'text-gray-700': !checked,
          'border-error-500': hasError,
          'border-gray-700': !hasError
        })}
        value={value}
        required
        onChange={(e) => onValueChnage(e.target.value)}
      />
    </div>
  );
}

/**
 * Threshold editor compoennt.
 * @param props - Properties of Threshold editor.
 * @returns React element.
 */
export default function ThresholdEditor(props: Props): ReactElement {
  const { data: propData, mode, parameter, isNew, onSave, disabled, onDelete } = props;
  const { userConfigDataV2, parameters } = useData();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string>('');
  const [data, setData] = useState<ThresholdValues>({} as ThresholdValues);
  const [focused, setFocused] = useState<string>('');

  useEffect(() => {
    setData(propData);
    setEditing(false);
  }, [propData]);

  const elementsRef = useRef<HTMLDivElement[]>([]);
  const [checked, setChecked] = useState<string[]>(['upper-critical', 'upper-warning']);

  // eslint-disable-next-line jsdoc/require-jsdoc
  const handleSave = (): void => {
    if (data.lowerCritical < data.min) {
      setError(`lower-critical-${parameter}`);
      toast.error("Lower critical can't be less than minimum value");
      return;
    }
    if (data.lowerWarning < data.min || data.lowerWarning < data.lowerCritical) {
      setError(`lower-warning-${parameter}`);
      toast.error("Lower warning can't be less than minimum value or lower critical");
      return;
    }
    if (data.upperCritical > data.max) {
      setError(`upper-critical-${parameter}`);
      toast.error("Upper critical can't be more than maximum value");
      return;
    }
    if (data.upperWarning > data.max || data.upperWarning > data.upperCritical) {
      setError(`upper-warning-${parameter}`);
      toast.error("Upper warning can't be more than maximum value or upper critical");
      return;
    }

    if (data.upperWarning <= data.lowerWarning) {
      setError(`upper-warning-${parameter}`);
      toast.error("Upper warning can't be less than or equal to lower warning");
      return;
    }
    if (data.upperCritical <= data.lowerWarning) {
      setError(`upper-critical-${parameter}`);
      toast.error("Upper critical can't be less than or equal to lower warning");
      return;
    }
    if (data.upperWarning <= data.lowerCritical) {
      setError(`upper-warning-${parameter}`);
      toast.error("Upper warning can't be less than or equal to lower critical");
      return;
    }
    if (data.upperCritical <= data.lowerCritical) {
      setError(`upper-critical-${parameter}`);
      toast.error("Upper critical can't be less than or equal to lower critical");
      return;
    }

    if (data.lowerWarning > data.upperWarning) {
      setError(`lower-warning-${parameter}`);
      toast.error("Lower warning can't be greater than upper warning");
      return;
    }
    if (data.lowerWarning > data.upperCritical) {
      setError(`lower-warning-${parameter}`);
      toast.error("Lower warning can't be greater than upper critical");
      return;
    }
    if (data.lowerCritical > data.upperCritical) {
      setError(`lower-critical-${parameter}`);
      toast.error("Lower critical can't be greater than upper critical");
      return;
    }
    if (data.lowerWarning > data.upperCritical) {
      setError(`lower-warning-${parameter}`);
      toast.error("Lower warning can't be greater than upper critical");
      return;
    }
    onSave(data);
    setEditing(false);
  };

  useEffect(() => {
    setError('');
  }, [editing]);

  return (
    <div className="bg-container-800 p-6 rounded flex flex-col w-[49%] h-fit text-gray-300">
      <div className="flex items-center justify-between">
        <div className="flex items-center text-white font-medium h-fit ">
          <div>{getParameterByName(parameter, parameters)?.shortName} </div>
          <span className="text-gray-500 text-center ml-2">{DEFAULT_UNITS[parameter]}</span>
        </div>
        {mode !== 'auto' && (
          <div className="flex justify-end">
            {editing ? (
              <div className="flex gap-3">
                <Button
                  onClick={handleSave}
                  className="h-9"
                  theme="primary"
                  size="xs"
                  name="Save"
                />
                <Button
                  onClick={() => setEditing(false)}
                  className="h-9"
                  theme="secondary-color"
                  size="xs"
                  name="Close"
                />
              </div>
            ) : (
              <div className="flex gap-3">
                <Button
                  theme="secondary-color"
                  size="xs"
                  disabled={disabled}
                  onClick={() => setEditing(true)}
                  name={data.incomplete ? 'Set thresholds' : 'Edit'}
                />
                <Button
                  theme="danger"
                  size="xs"
                  disabled={disabled}
                  onClick={onDelete}
                  name="Delete"
                />
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex justify-between items-center mt-8 gap-2">
        <div className="text-gray-400 w-16 flex justify-start">{data.min}</div>

        <div className="text-gray-400 w-16 flex justify-end">{data.max}</div>
      </div>
      <div className="flex justify-between items-center mt-2 gap-2">
        {(editing || data.lowerCritical !== data.min) && (
          <div
            className={`flex flex-1 h-1 ${
              mode === 'custom' && data.incomplete ? 'bg-gray-600' : 'bg-error-500'
            }`}
          />
        )}
        {(editing || data.lowerCritical !== data.min) &&
          (focused === `lower-critical-${parameter}` ? (
            <div
              style={{ background: 'rgba(25, 144, 255, 0.1)' }}
              className="flex w-7 h-7 justify-center items-center relative rounded-full"
            >
              <div className="flex w-4 h-4 bg-primary-500 rounded-full" />
              <div className="text-gray-300 top-[-25px] absolute font-semibold">
                {data.lowerCritical}
              </div>
            </div>
          ) : (
            <div className="flex w-[2px] h-4 bg-container-200 relative">
              <div className="text-gray-300 top-[-25px] absolute font-semibold">
                {data.lowerCritical}
              </div>
            </div>
          ))}
        {(editing || data.lowerWarning !== data.lowerCritical) && (
          <div
            className={`flex flex-1 h-1 ${
              mode === 'custom' && data.incomplete ? 'bg-gray-600' : 'bg-warning-500'
            }`}
          />
        )}
        {(editing || data.lowerWarning !== data.lowerCritical) &&
          (focused === `lower-warning-${parameter}` ? (
            <div
              style={{ background: 'rgba(25, 144, 255, 0.1)' }}
              className="flex w-7 h-7 justify-center items-center relative rounded-full"
            >
              <div className="flex w-4 h-4 bg-primary-500 rounded-full" />
              <div className="text-gray-300 top-[-25px] absolute font-semibold">
                {data.lowerWarning}
              </div>
            </div>
          ) : (
            <div className="flex w-[2px] h-4 bg-container-200 relative">
              <div className="text-gray-300 top-[-25px] absolute font-semibold">
                {data.lowerWarning}
              </div>
            </div>
          ))}
        <div
          className={`flex flex-1 h-1 ${
            mode === 'custom' && data.incomplete ? 'bg-gray-600' : 'bg-success-500'
          }`}
        />

        {(editing || data.upperWarning !== data.lowerCritical) &&
        focused === `upper-warning-${parameter}` ? (
          <div
            style={{ background: 'rgba(25, 144, 255, 0.1)' }}
            className="flex w-7 h-7 justify-center items-center relative rounded-full"
          >
            <div className="flex w-4 h-4 bg-primary-500 rounded-full" />
            <div className="text-gray-300 top-[-25px] absolute font-semibold">
              {data.upperWarning}
            </div>
          </div>
        ) : (
          <div className="flex w-[2px] h-4 bg-container-200 relative">
            <div className="text-gray-300 top-[-25px] absolute font-semibold">
              {data.upperWarning}
            </div>
          </div>
        )}

        {(editing || data.upperWarning !== data.upperCritical) && (
          <div
            className={`flex flex-1 h-1 ${
              mode === 'custom' && data.incomplete ? 'bg-gray-600' : 'bg-warning-500'
            }`}
          />
        )}
        {(editing || data.upperWarning !== data.upperCritical) &&
          (focused === `upper-critical-${parameter}` ? (
            <div
              style={{ background: 'rgba(25, 144, 255, 0.1)' }}
              className="flex w-7 h-7 justify-center items-center relative rounded-full"
            >
              <div className="flex w-4 h-4 bg-primary-500 rounded-full" />
              <div className="text-gray-300 top-[-25px] absolute font-semibold">
                {data.upperCritical}
              </div>
            </div>
          ) : (
            <div className="flex w-[2px] h-4 bg-container-200 relative">
              <div className="text-gray-300 top-[-25px] absolute font-semibold">
                {data.upperCritical}
              </div>
            </div>
          ))}
        {(editing || data.upperCritical !== data.max) && (
          <div
            className={`flex flex-1 h-1 ${
              mode === 'custom' && data.incomplete ? 'bg-gray-600' : 'bg-error-500'
            }`}
          />
        )}
      </div>
      {editing && (
        <div className="flex justify-between flex-wrap">
          <ValueEditor
            hasError={error === `lower-critical-${parameter}`}
            id={`lower-critical-${parameter}`}
            onFocusChange={(val) => setFocused(val)}
            min={data.min.toString()}
            max={data.max.toString()}
            setChecked={(val) =>
              setChecked((prev) =>
                val ? [...prev, 'lower-critical'] : prev.filter((item) => item !== 'lower-critical')
              )
            }
            label="Lower critical"
            checked={checked.includes('lower-critical')}
            type="critical"
            value={data.lowerCritical?.toString() || ''}
            onChange={(text) => {
              const value = text ? parseFloat(text) : 0;
              setData({
                ...data,
                lowerCritical: value
              });
            }}
          />

          <ValueEditor
            hasError={error === `lower-warning-${parameter}`}
            id={`lower-warning-${parameter}`}
            onFocusChange={(val) => setFocused(val)}
            label="Lower warning"
            min={data.min.toString()}
            setChecked={(val) =>
              setChecked((prev) =>
                val ? [...prev, 'lower-warning'] : prev.filter((item) => item !== 'lower-warning')
              )
            }
            max={data.max.toString()}
            checked={checked.includes('lower-warning')}
            type="warning"
            value={data.lowerWarning?.toString() || ''}
            onChange={(text) => setData({ ...data, lowerWarning: text ? parseFloat(text) : 0 })}
          />
          <ValueEditor
            hasError={error === `upper-warning-${parameter}`}
            id={`upper-warning-${parameter}`}
            onFocusChange={(val) => setFocused(val)}
            setChecked={(val) =>
              setChecked((prev) =>
                val ? [...prev, 'upper-warning'] : prev.filter((item) => item !== 'upper-warning')
              )
            }
            min={data.min.toString()}
            max={data.max.toString()}
            checked={checked.includes('upper-warning')}
            label="Upper warning"
            type="warning"
            value={data.upperWarning?.toString() || ''}
            onChange={(text) => setData({ ...data, upperWarning: text as unknown as number })}
          />
          <ValueEditor
            hasError={error === `upper-critical-${parameter}`}
            id={`upper-critical-${parameter}`}
            onFocusChange={(val) => setFocused(val)}
            min={data.min.toString()}
            checked={checked.includes('upper-critical')}
            max={data.max.toString()}
            setChecked={(val) =>
              setChecked((prev) =>
                val ? [...prev, 'upper-critical'] : prev.filter((item) => item !== 'upper-critical')
              )
            }
            label="Upper critical"
            type="critical"
            value={data.upperCritical?.toString() || ''}
            onChange={(text) => {
              setData({
                ...data,
                upperCritical: text as unknown as number
              });
            }}
          />
        </div>
      )}
    </div>
  );
}
